// Localization strings for Morafaa Lawyer Website
export interface LocalizedStrings {
  // Common
  common: {
    loading: string;
    error: string;
    success: string;
    save: string;
    saving: string;
    cancel: string;
    edit: string;
    delete: string;
    copy: string;
    share: string;
    close: string;
    back: string;
    next: string;
    previous: string;
    search: string;
    filter: string;
    refresh: string;
    view: string;
    details: string;
    select: string;
    confirm: string;
    yes: string;
    no: string;
    required: string;
    title: string;
    description: string;
    tags: string;
    settings: string;
  };

  // Login
  login: {
    title: string;
    welcomeBack: string;
    subtitle: string;
    signIn: string;
    signInSubtitle: string;
    email: string;
    emailPlaceholder: string;
    password: string;
    passwordPlaceholder: string;
    forgotPassword: string;
    signingIn: string;
    resetPassword: string;
    resetPasswordSubtitle: string;
    sendResetEmail: string;
    sending: string;
    noAccount: string;
    createAccount: string;
    copyright: string;
    language: string;
  };

  // Sign Up
  signup: {
    title: string;
    welcome: string;
    subtitle: string;
    createAccount: string;
    createAccountSubtitle: string;
    firstName: string;
    firstNamePlaceholder: string;
    lastName: string;
    lastNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    password: string;
    passwordPlaceholder: string;
    confirmPassword: string;
    confirmPasswordPlaceholder: string;
    creatingAccount: string;
    haveAccount: string;
    signIn: string;
    copyright: string;
    language: string;
    passwordMismatch: string;
    weakPassword: string;
    emailInUse: string;
    invalidEmail: string;
    required: string;
  };

  // Search
  search: {
    general: string;
    users: string;
    offices: string;
    clients: string;
    legalFiles: string;
    events: string;
    logs: string;
    presetData: string;
  };

  // Navigation
  navigation: {
    dashboard: string;
    agenda: string;
    myOffices: string;
    myClients: string;
    legalFiles: string;
    teamMembers: string;
    lawyerGuide: string;
    statistics: string;
    community: string;
    subscriptions: string;
    myProfile: string;
    referralProgram: string;
    support: string;
  };

  // Dashboard
  dashboard: {
    title: string;
    welcome: string;
    loading: string;
    loadingRoles: string;
    loadingStats: string;
    errorTitle: string;
    loadError: string;
    lastUpdated: string;
    updating: string;
    myClients: string;
    myOffices: string;
    upcomingEvents: string;
    legalFiles: string;
    recentActivity: string;
    live: string;
    active: string;
    next30Days: string;
    documents: string;
    transactions: string;
    last7Days: string;
    // Enhanced Dashboard
    noData: string;
    refresh: string;
    // Time Range Selector
    '7d': string;
    '30d': string;
    '90d': string;
    '1y': string;
    // Financial Overview
    financialOverview: string;
    totalRevenue: string;
    totalExpenses: string;
    netProfit: string;
    revenue: string;
    expenses: string;
    profit: string;
    revenueExpensesProfitTrend: string;
    outstandingInvoices: string;
    overdueInvoices: string;
    // Client Analytics
    clientAnalytics: string;
    acquisitionFunnel: string;
    clientValueDistribution: string;
    clientGrowthRate: string;
    newClientsLast30Days: string;
    clients: string;
    count: string;
    // Case Performance
    casePerformance: string;
    caseStatusDistribution: string;
    winRate: string;
    averageCaseDuration: string;
    days: string;
    // Office Performance
    officePerformance: string;
    files: string;
    // Predictive Analytics
    predictiveAnalytics: string;
    predictedRevenueNext3Months: string;
    noPredictionData: string;
    // Chart Labels
    increase: string;
    decrease: string;
    noChange: string;
    // Status Labels
    statusActive: string;
    statusPending: string;
    statusClosed: string;
    statusWon: string;
    statusLost: string;
    // Value Ranges
    highValue: string;
    mediumValue: string;
    lowValue: string;
    // Data Availability
    dataNotice: string;
    dataNoticeDescription: string;
    totalCases: string;
    activeCases: string;
  };


  // Profile
  profile: {
    title: string;
    personalInfo: string;
    firstName: string;
    middleName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    phoneNumber2: string;
    nationalID: string;
    birthdate: string;
    gender: string;
    male: string;
    female: string;
    address: string;
    photo: string;
    uploadPhoto: string;
    saveChanges: string;
    cancelChanges: string;
    editProfile: string;
    profileUpdated: string;
    updateError: string;
  };

  // My Offices
  myOffices: {
    title: string;
    selectOffice: string;
    selectOfficeTitle: string;
    selectOfficeDescription: string;
    officeDetails: string;
    officeName: string;
    roleName: string;
    roleDescription: string;
    permissions: string;
    noOffices: string;
    addOffice: string;
    addNewOffice: string;
    step1Title: string;
    step1Description: string;
    step2Title: string;
    step2Description: string;
    step3Title: string;
    // Office Details Panel
    officeInformation: string;
    officeAddress: string;
    officePhone: string;
    officeEmail: string;
    officeWebsite: string;
    yourRoles: string;
    roles: string;
    rolesPlural: string;
    joined: string;
    creator: string;
    admin: string;
    salaryType: string;
    salaryValue: string;
    profitPercentage: string;
    specialty: string;
    status: string;
    studentIntern: string;
    fixedSalary: string;
    percentageBased: string;
    fixedPercentage: string;
    generalPractice: string;
    legalProfessional: string;
    unknown: string;
    archived: string;
    active: string;
    // Edit Office Modal
    editOffice: string;
    roleDetails: string;
    lawyerTitle: string;
    salaryValueRequired: string;
    fixedSalaryAmount: string;
    percentageAmount: string;
    update: string;
    updating: string;
    updateError: string;
    canEditRole: string;
    cannotEditRole: string;
    actions: string;
    step3Description: string;
    searchPlaceholder: string;
    entityNotFound: string;
    createNewEntity: string;
    noDataAvailable: string;
    pullToRefresh: string;
    entityType: string;
    alternativeName: string;
    phoneNumber: string;
    secondPhoneNumber: string;
    address: string;
    website: string;
    socialMedia: string;
    subSpecialty: string;
    isOwner: string;
    yes: string;
    no: string;
    save: string;
    next: string;
    previous: string;
    cancel: string;
  };

  // My Clients
  myClients: {
    title: string;
    selectRole: string;
    clientList: string;
    clientDetails: string;
    clientName: string;
    phoneNumber: string;
    email: string;
    address: string;
    noClients: string;
    noRoleSelected: string;
    addNewClient: string;
    addNewClientTitle: string;
    step1Title: string;
    step1Description: string;
    step2Title: string;
    step2Description: string;
    searchByPhone: string;
    phonePlaceholder: string;
    searchButton: string;
    searching: string;
    clientFound: string;
    clientFoundMessage: string;
    addToOffice: string;
    adding: string;
    clientAlreadyAssociated: string;
    clientAlreadyAssociatedMessage: string;
    tryAnotherNumber: string;
    noClientFound: string;
    noClientFoundMessage: string;
    createNewClient: string;
    tryAgain: string;
    fullName: string;
    fullNamePlaceholder: string;
    mobileNumber: string;
    mobileNumberPlaceholder: string;
    emailLabel: string;
    emailPlaceholder: string;
    birthDate: string;
    birthDatePlaceholder: string;
    selectedDate: string;
    gender: string;
    male: string;
    female: string;
    creating: string;
    next: string;
    editClient: string;
    editClientTitle: string;
    updateClient: string;
    updating: string;
    maritalStatus: string;
    selectMaritalStatus: string;
    single: string;
    married: string;
    divorced: string;
    widowed: string;
    nationalID: string;
    nationalIDPlaceholder: string;
    secondaryPhone: string;
    secondaryPhonePlaceholder: string;
    saveChanges: string;
  };

  // Legal Files
  legalFiles: {
    title: string;
    subtitle: string;
    selectRole: string;
    selectClient: string;
    legalFilesList: string;
    fileNumber: string;
    fileTitle: string;
    fileType: string;
    status: string;
    createdDate: string;
    lastUpdated: string;
    overview: string;
    agenda: string;
    attachments: string;
    noFiles: string;
    noClientSelected: string;
    noRoleSelected: string;
    addNewFile: string;
    addNewFileTitle: string;
    addNewFileSubtitle: string;
    basicInformation: string;
    caseInformation: string;
    financialInformation: string;
    additionalNotes: string;
    fileTitleLabel: string;
    fileTitlePlaceholder: string;
    fileTypeLabel: string;
    selectFileType: string;
    statusLabel: string;
    selectStatus: string;
    caseNumberLabel: string;
    caseNumberPlaceholder: string;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    caseYearLabel: string;
    caseYearPlaceholder: string;
    courtTypeLabel: string;
    selectCourtType: string;
    caseAreaLabel: string;
    caseAreaPlaceholder: string;
    firstSessionDateLabel: string;
    caseOpponentsLabel: string;
    caseOpponentsPlaceholder: string;
    expectedFeesLabel: string;
    totalIncomeLabel: string;
    totalExpensesLabel: string;
    notesLabel: string;
    notesPlaceholder: string;
    caseTypeLabel: string;
    selectCaseType: string;
    mainSubjectLabel: string;
    mainSubjectPlaceholder: string;
    createFile: string;
    creating: string;
    fileTitleRequired: string;
    descriptionRequired: string;
    caseNumberRequired: string;
    fileTypeRequired: string;
    statusRequired: string;
    selectRoleClientFirst: string;
    editFile: string;
    archiveFile: string;
    unarchiveFile: string;
    editFileTitle: string;
    archiveFileTitle: string;
    unarchiveFileTitle: string;
    archiveFileMessage: string;
    unarchiveFileMessage: string;
    confirmArchive: string;
    confirmUnarchive: string;
    cancelArchive: string;
    cancelEdit: string;
    saveFile: string;
    saving: string;
  };

  // Community
  community: {
    title: string;
    subtitle: string;
    searchPlaceholder: string;
    forums: string;
    events: string;
    network: string;
    forumsDescription: string;
    eventsDescription: string;
    networkDescription: string;
    // Forums
    createForum: string;
    allCategories: string;
    noForums: string;
    noForumsSearch: string;
    createFirstForum: string;
    discussionForums: string;
    participateInDiscussions: string;
    // Events
    createEvent: string;
    allTypes: string;
    allTime: string;
    upcoming: string;
    today: string;
    thisWeek: string;
    noEvents: string;
    noEventsSearch: string;
    createFirstEvent: string;
    professionalEvents: string;
    attendSeminars: string;
    // Network
    allSpecialties: string;
    allExperience: string;
    connectedOnly: string;
    lawyers: string;
    noLawyers: string;
    noLawyersSearch: string;
    noLawyersNetwork: string;
    professionalNetwork: string;
    buildRelationships: string;
    // Common
    viewDetails: string;
    viewProfile: string;
    connect: string;
    connected: string;
    pending: string;
    declined: string;
    rsvp: string;
    yearsExperience: string;
    languages: string;
    // Modals
    createNewForum: string;
    createNewEvent: string;
    underDevelopment: string;
    close: string;
    // Forum Creation
    rules: string;
    isPublic: string;
    allowThreadCreation: string;
    requireApproval: string;
    color: string;
    icon: string;
    // Thread Types
    discussion: string;
    question: string;
    announcement: string;
    poll: string;
    // Stats
    posts: string;
    comments: string;
    attendees: string;
    connections: string;
    rating: string;
    reviews: string;
    // Time
    lastPost: string;
    registeredAt: string;
    // Status
    published: string;
    cancelled: string;
    completed: string;
    draft: string;
    // Location
    onlineEvent: string;
    link: string;
    organizedBy: string;
    // Experience ranges
    experience0to2: string;
    experience3to5: string;
    experience6to10: string;
    experience10plus: string;
  };

  // Events/Agenda
  events: {
    title: string;
    calendarView: string;
    listView: string;
    today: string;
    noEvents: string;
    createNewEvent: string;
    editEvent: string;
    deleteEvent: string;
    addNewEvent: string;
    selectOffice: string;
    selectClient: string;
    selectLegalFile: string;
    fileTitle: string;
    enterFileTitle: string;
    eventType: string;
    eventTitle: string;
    eventDate: string;
    eventTime: string;
    eventLocation: string;
    eventDescription: string;
    previousMonth: string;
    nextMonth: string;
    previousMonthArrow: string;
    nextMonthArrow: string;
    // Event types
    eventTypes: {
      clientMeeting: string;
      courtSession: string;
      governmental: string;
    };
    // Event statuses
    eventStatuses: {
      upcoming: string;
      inProgress: string;
      completed: string;
      cancelled: string;
      expired: string;
    };
    // Place types
    placeTypes: {
      office: string;
      court: string;
      clientLocation: string;
      government: string;
      other: string;
    };
    // Calendar localization
    daysOfWeek: {
      sunday: string;
      monday: string;
      tuesday: string;
      wednesday: string;
      thursday: string;
      friday: string;
      saturday: string;
    };
    daysOfWeekShort: {
      sunday: string;
      monday: string;
      tuesday: string;
      wednesday: string;
      thursday: string;
      friday: string;
      saturday: string;
    };
    months: {
      january: string;
      february: string;
      march: string;
      april: string;
      may: string;
      june: string;
      july: string;
      august: string;
      september: string;
      october: string;
      november: string;
      december: string;
    };
    monthsShort: {
      january: string;
      february: string;
      march: string;
      april: string;
      may: string;
      june: string;
      july: string;
      august: string;
      september: string;
      october: string;
      november: string;
      december: string;
    };
  };

  // Support
  support: {
    title: string;
    aboutMorafaa: string;
    aboutDescription: string;
    connectWithUs: string;
    facebook: string;
    instagram: string;
    customerSupport: string;
    whatsappSupport: string;
    legalInformation: string;
    termsConditions: string;
    privacyPolicy: string;
    viewTerms: string;
    viewPrivacy: string;
    english: string;
    arabic: string;
    contactUs: string;
    inquiryType: string;
    selectType: string;
    message: string;
    messagePlaceholder: string;
    send: string;
    sending: string;
    inquirySent: string;
    inquirySentMessage: string;
    errorSending: string;
    errorSendingMessage: string;
  };

  // Referral Program
  referral: {
    title: string;
    subtitle: string;
    headerDescription: string;
    howItWorks: string;
    step1: string;
    step2: string;
    step3: string;
    yourCode: string;
    yourInviteLink: string;
    copy: string;
    copyLink: string;
    share: string;
    copied: string;
    totalReferrals: string;
    successfulReferrals: string;
    freeMonthsEarned: string;
    monthsOfFreeService: string;
    termsTitle: string;
    terms: string[];
    shareMessage: string;
    shareTitle: string;
    statsEmpty: string;
    statsError: string;
    statsRefresh: string;
  };


  // Team Members
  teamMembers: {
    title: string;
    selectOffice: string;
    teamMembersList: string;
    memberDetails: string;
    memberName: string;
    memberEmail: string;
    memberPhone: string;
    memberRole: string;
    permissions: string;
    accessClients: string;
    accessLegalFiles: string;
    accessEvents: string;
    noMembers: string;
    noOfficeSelected: string;
    addMember: string;
    editMember: string;
    removeMember: string;
    updatePermissions: string;
    permissionsUpdated: string;
    memberAdded: string;
    memberRemoved: string;
    errorUpdatingPermissions: string;
    errorAddingMember: string;
    errorRemovingMember: string;
  };

  // Lawyer Guide
  lawyerGuide: {
    title: string;
    subtitle: string;
    categories: string;
    resources: string;
    searchPlaceholder: string;
    noCategories: string;
    noResources: string;
    selectCategory: string;
    noResults: string;
    openInNewTab: string;
    externalLink: string;
    categoryDescription: string;
    resourceDescription: string;
    loadingCategories: string;
    loadingResources: string;
    errorLoadingCategories: string;
    errorLoadingResources: string;
    searchFailed: string;
  };

  // Subscriptions
  subscriptions: {
    title: string;
    subtitle: string;
    currentSubscription: string;
    usageDashboard: string;
    planComparison: string;
    noActiveSubscription: string;
    noActiveSubscriptionMessage: string;
    viewPlans: string;
    manageSubscription: string;
    refreshData: string;
    quickActions: string;
    upgradePlan: string;
    choosePlan: string;
    currentPlan: string;
    mostPopular: string;
    freeTrial: string;
    dayTrial: string;
    daysLeft: string;
    trialDaysLeft: string;
    startDate: string;
    endDate: string;
    autoRenew: string;
    yes: string;
    no: string;
    active: string;
    trial: string;
    expired: string;
    cancelled: string;
    pastDue: string;
    loading: string;
    loadingSubscription: string;
    loadingUsageStats: string;
    failedToLoad: string;
    tryAgain: string;
    close: string;
    // Plan features
    planFeatures: string;
    offices: string;
    clients: string;
    files: string;
    unlimited: string;
    perOffice: string;
    perClient: string;
    eventReminders: string;
    advancedStats: string;
    communityAccess: string;
    phoneSupport: string;
    teamMembers: string;
    perMember: string;
    // Usage warnings
    usageWarnings: string;
    approachingLimit: string;
    // Plan names
    freePlan: string;
    basicPlan: string;
    professionalPlan: string;
    // Plan descriptions
    freePlanDescription: string;
    basicPlanDescription: string;
    professionalPlanDescription: string;
  };

  // Offices
  offices: {
    title: string;
    subtitle: string;
    loading: string;
    loadingOffices: string;
    errorTitle: string;
    loadError: string;
    demoMode: string;
    exportExcel: string;
    refreshing: string;
    refresh: string;
    allStatus: string;
    activeStatus: string;
    archived: string;
    deleted: string;
    noOffices: string;
    noOfficesFound: string;
    tryAdjusting: string;
    noOfficesCreated: string;
    officeDetails: string;
    contactInformation: string;
    address: string;
    phone: string;
    email: string;
    website: string;
    statusDates: string;
    status: string;
    created: string;
    updated: string;
    officeStaff: string;
    loadingStaff: string;
    retry: string;
    noStaffMembers: string;
    noStaffAssigned: string;
    inactive: string;
    activeStaff: string;
    noPhone: string;
    noEmail: string;
    officeName: string;
    alternativeName: string;
    description: string;
    optionalEmail: string;
    officeDescription: string;
    logoURL: string;
    briefDescription: string;
    logoPlaceholder: string;
    searchPlaceholder: string;
    failedToLoadUsers: string;
    // Add Office Modal
    searching: string;
    selectSubSpecialty: string;
    lawyerTitle: string;
    salaryConfiguration: string;
    salaryType: string;
    studentIntern: string;
    fixedSalary: string;
    percentageBased: string;
    fixedAndPercentage: string;
    fixedSalaryEGP: string;
    profitPercentage: string;
    officeNameRequired: string;
    phoneNumberRequired: string;
    validEmailAddress: string;
    validSalaryAmount: string;
    validProfitPercentage: string;
    // Sub-specialty options
    personalStatus: string;
    commercial: string;
    criminal: string;
    civil: string;
    administrative: string;
    labor: string;
    tax: string;
    realEstate: string;
    intellectualProperty: string;
    family: string;
  };
}

// Arabic strings (default)
export const stringsAr: LocalizedStrings = {
  common: {
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجح',
    save: 'حفظ',
    saving: 'جاري الحفظ...',
    cancel: 'إلغاء',
    edit: 'تعديل',
    delete: 'حذف',
    copy: 'نسخ',
    share: 'مشاركة',
    close: 'إغلاق',
    back: 'رجوع',
    next: 'التالي',
    previous: 'السابق',
    search: 'بحث',
    filter: 'تصفية',
    refresh: 'تحديث',
    view: 'عرض',
    details: 'التفاصيل',
    select: 'اختر',
    confirm: 'تأكيد',
    yes: 'نعم',
    no: 'لا',
    required: 'هذا الحقل مطلوب',
    title: 'العنوان',
    description: 'الوصف',
    tags: 'العلامات',
    settings: 'الإعدادات',
  },

  // Login
  login: {
    title: 'تسجيل الدخول',
    welcomeBack: 'مرحباً بعودتك',
    subtitle: 'سجل دخولك إلى حساب مرافعة للمتابعة',
    signIn: 'تسجيل الدخول',
    signInSubtitle: 'أدخل بياناتك للوصول إلى حسابك',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'أدخل عنوان بريدك الإلكتروني',
    password: 'كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    forgotPassword: 'نسيت كلمة المرور؟',
    signingIn: 'جاري تسجيل الدخول...',
    resetPassword: 'إعادة تعيين كلمة المرور',
    resetPasswordSubtitle: 'أدخل عنوان بريدك الإلكتروني وسنرسل لك رابطاً لإعادة تعيين كلمة المرور.',
    sendResetEmail: 'إرسال رابط إعادة التعيين',
    sending: 'جاري الإرسال...',
    noAccount: 'ليس لديك حساب؟',
    createAccount: 'إنشاء حساب',
    copyright: '© 2025 مرافعة. جميع الحقوق محفوظة.',
    language: 'اللغة',
  },

  // Sign Up
  signup: {
    title: 'إنشاء حساب',
    welcome: 'مرحباً بك',
    subtitle: 'أنشئ حسابك في مرافعة للبدء',
    createAccount: 'إنشاء حساب',
    createAccountSubtitle: 'أدخل بياناتك لإنشاء حساب جديد',
    firstName: 'الاسم الأول',
    firstNamePlaceholder: 'أدخل اسمك الأول',
    lastName: 'الاسم الأخير',
    lastNamePlaceholder: 'أدخل اسمك الأخير',
    email: 'البريد الإلكتروني',
    emailPlaceholder: 'أدخل عنوان بريدك الإلكتروني',
    phone: 'رقم الهاتف',
    phonePlaceholder: 'أدخل رقم هاتفك',
    password: 'كلمة المرور',
    passwordPlaceholder: 'أدخل كلمة المرور',
    confirmPassword: 'تأكيد كلمة المرور',
    confirmPasswordPlaceholder: 'أعد إدخال كلمة المرور',
    creatingAccount: 'جاري إنشاء الحساب...',
    haveAccount: 'لديك حساب بالفعل؟',
    signIn: 'تسجيل الدخول',
    copyright: '© 2025 مرافعة. جميع الحقوق محفوظة.',
    language: 'اللغة',
    passwordMismatch: 'كلمات المرور غير متطابقة',
    weakPassword: 'كلمة المرور ضعيفة جداً',
    emailInUse: 'البريد الإلكتروني مستخدم بالفعل',
    invalidEmail: 'عنوان بريد إلكتروني غير صحيح',
    required: 'هذا الحقل مطلوب',
  },

  search: {
    general: 'بحث...',
    users: 'البحث عن طريق معرف المستخدم، البريد الإلكتروني، الاسم، أو الهاتف...',
    offices: 'البحث عن طريق معرف جهة العمل، الاسم، الهاتف، أو البريد الإلكتروني...',
    clients: 'البحث عن طريق معرف الموكل، الاسم، البريد الإلكتروني، أو الهاتف...',
    legalFiles: 'البحث عن طريق معرف الملف، العنوان، أو اسم المحامي...',
    events: 'البحث عن طريق معرف الحدث أو اسم المحامي...',
    logs: 'البحث عن طريق مستوى السجل، الفئة، أو الرسالة...',
    presetData: 'البحث في البيانات المحددة مسبقاً...',
  },

  navigation: {
    dashboard: 'الإحصائيات المتقدمة',
    agenda: 'الأجندة',
    myOffices: 'جهات العمل',
    myClients: 'الموكلين',
    legalFiles: 'الملفات القانونية',
    teamMembers: 'فريق العمل',
    lawyerGuide: 'دليل المحامي',
    statistics: 'الإحصائيات',
    community: 'المجتمع',
    subscriptions: 'الاشتراكات',
    myProfile: 'ملفي الشخصي',
    referralProgram: 'برنامج الإحالة',
    support: 'مركز الدعم',
  },

  dashboard: {
    title: 'لوحة التحكم المتقدمة',
    welcome: 'مرحباً بك في نظام مرافعة لإدارة الممارسة القانونية',
    loading: 'جاري تحميل لوحة التحكم',
    loadingRoles: 'جاري تحميل أدوارك ومكاتبك...',
    loadingStats: 'جاري جلب الإحصائيات المباشرة...',
    errorTitle: 'خطأ في تحميل لوحة التحكم',
    loadError: 'فشل في تحميل إحصائيات لوحة التحكم. يرجى المحاولة مرة أخرى.',
    lastUpdated: 'آخر تحديث',
    updating: 'جاري تحديث إحصائيات لوحة التحكم...',
    myClients: 'موكلي',
    myOffices: 'جهات العمل',
    upcomingEvents: 'الأحداث القادمة',
    legalFiles: 'الملفات القانونية',
    recentActivity: 'النشاط الأخير',
    live: 'مباشر',
    active: 'نشط',
    next30Days: 'الـ 30 يوم القادمة',
    documents: 'مستندات',
    transactions: 'معاملات',
    last7Days: 'آخر 7 أيام',
    // Enhanced Dashboard
    noData: 'لا توجد بيانات متاحة',
    refresh: 'تحديث',
    // Time Range Selector
    '7d': '7 أيام',
    '30d': '30 يوم',
    '90d': '90 يوم',
    '1y': 'سنة واحدة',
    // Financial Overview
    financialOverview: 'نظرة عامة مالية',
    totalRevenue: 'إجمالي الإيرادات',
    totalExpenses: 'إجمالي المصروفات',
    netProfit: 'صافي الربح',
    revenue: 'الإيرادات',
    expenses: 'المصروفات',
    profit: 'الربح',
    revenueExpensesProfitTrend: 'اتجاه الإيرادات والمصروفات والربح',
    outstandingInvoices: 'الفواتير المستحقة',
    overdueInvoices: 'الفواتير المتأخرة',
    // Client Analytics
    clientAnalytics: 'تحليلات الموكلين',
    acquisitionFunnel: 'قمع اكتساب الموكلين',
    clientValueDistribution: 'توزيع قيمة الموكلين',
    clientGrowthRate: 'معدل نمو الموكلين',
    newClientsLast30Days: 'موكلون جدد آخر 30 يوم',
    clients: 'الموكلون',
    count: 'العدد',
    // Case Performance
    casePerformance: 'أداء القضايا',
    caseStatusDistribution: 'توزيع حالة القضايا',
    winRate: 'معدل الفوز',
    averageCaseDuration: 'متوسط مدة القضية',
    days: 'أيام',
    // Office Performance
    officePerformance: 'أداء جهات العمل',
    files: 'الملفات',
    // Predictive Analytics
    predictiveAnalytics: 'التحليلات التنبؤية',
    predictedRevenueNext3Months: 'الإيرادات المتوقعة للـ 3 أشهر القادمة',
    noPredictionData: 'لا توجد بيانات تنبؤية متاحة',
    // Chart Labels
    increase: 'زيادة',
    decrease: 'انخفاض',
    noChange: 'بدون تغيير',
    // Status Labels
    statusActive: 'نشط',
    statusPending: 'معلق',
    statusClosed: 'مغلق',
    statusWon: 'فائز',
    statusLost: 'خاسر',
    // Value Ranges
    highValue: 'قيمة عالية',
    mediumValue: 'قيمة متوسطة',
    lowValue: 'قيمة منخفضة',
    // Data Availability
    dataNotice: 'ملاحظة حول البيانات',
    dataNoticeDescription: 'يتم عرض الإحصائيات والرسوم البيانية بناءً على البيانات المتاحة فعلياً في قاعدة البيانات. بعض المقاييس قد لا تكون متاحة حالياً.',
    totalCases: 'إجمالي القضايا',
    activeCases: 'القضايا النشطة',
  },


  profile: {
    title: 'ملفي الشخصي',
    personalInfo: 'المعلومات الشخصية',
    firstName: 'الاسم الأول',
    middleName: 'الاسم الأوسط',
    lastName: 'اسم العائلة',
    email: 'البريد الإلكتروني',
    phoneNumber: 'رقم الهاتف',
    phoneNumber2: 'رقم الهاتف الثاني',
    nationalID: 'رقم الهوية الوطنية',
    birthdate: 'تاريخ الميلاد',
    gender: 'الجنس',
    male: 'ذكر',
    female: 'أنثى',
    address: 'العنوان',
    photo: 'الصورة',
    uploadPhoto: 'رفع صورة',
    saveChanges: 'حفظ التغييرات',
    cancelChanges: 'إلغاء التغييرات',
    editProfile: 'تعديل الملف الشخصي',
    profileUpdated: 'تم تحديث الملف الشخصي بنجاح!',
    updateError: 'فشل في تحديث الملف الشخصي. يرجى المحاولة مرة أخرى.',
  },

  myOffices: {
    title: 'جهات العمل',
    selectOffice: 'اختر جهة العمل',
    selectOfficeTitle: 'اختر جهة العمل',
    selectOfficeDescription: 'اختر جهة العمل من القائمة لعرض التفاصيل وأدوارك',
    officeDetails: 'تفاصيل جهة العمل',
    officeName: 'اسم جهة العمل',
    roleName: 'اسم الدور',
    roleDescription: 'وصف الدور',
    permissions: 'الصلاحيات',
    noOffices: 'لا توجد جهات عمل',
    addOffice: 'إضافة مكتب',
    addNewOffice: 'إضافة مؤسسة قانونية جديدة',
    step1Title: 'البحث عن المؤسسة',
    step1Description: 'ابحث عن ملف المؤسسة إذا كان موجودًا من قبل على نظام مرافعة',
    step2Title: 'المعلومات الأساسية للمؤسسة',
    step2Description: 'املأ المعلومات الأساسية للمؤسسة القانونية',
    step3Title: 'تعديل الدور',
    // Office Details Panel
    officeInformation: 'معلومات جهة العمل',
    officeAddress: 'العنوان',
    officePhone: 'الهاتف',
    officeEmail: 'البريد الإلكتروني',
    officeWebsite: 'الموقع الإلكتروني',
    yourRoles: 'أدوارك في هذه جهة العمل',
    roles: 'دور',
    rolesPlural: 'أدوار',
    joined: 'انضم في',
    creator: 'المنشئ',
    admin: 'مدير',
    salaryType: 'نوع الراتب',
    salaryValue: 'قيمة الراتب',
    profitPercentage: 'نسبة الربح',
    specialty: 'التخصص',
    status: 'الحالة',
    studentIntern: 'طالب/متدرب',
    fixedSalary: 'راتب ثابت',
    percentageBased: 'نسبة مئوية',
    fixedPercentage: 'ثابت + نسبة',
    generalPractice: 'ممارسة عامة',
    legalProfessional: 'محترف قانوني',
    unknown: 'غير معروف',
    archived: 'مؤرشف',
    active: 'نشط',
    // Edit Office Modal
    editOffice: 'تعديل جهة العمل',
    roleDetails: 'تفاصيل الدور',
    lawyerTitle: 'اللقب القانوني',
    salaryValueRequired: 'قيمة الراتب مطلوبة',
    fixedSalaryAmount: 'مبلغ الراتب الثابت',
    percentageAmount: 'مبلغ النسبة المئوية',
    update: 'تحديث',
    updating: 'جاري التحديث...',
    updateError: 'فشل في تحديث جهة العمل. يرجى المحاولة مرة أخرى.',
    canEditRole: 'يمكنك تعديل معلومات دورك',
    cannotEditRole: 'يمكنك التعديل فقط عندما تكون العضو الوحيد في الفريق',
    actions: 'الإجراءات',
    step3Description: 'حدد دورك في هذه المؤسسة',
    searchPlaceholder: 'مكتب',
    entityNotFound: 'المؤسسة القانونية غير موجودة؟',
    createNewEntity: 'إنشاء المؤسسة القانونية جديدة',
    noDataAvailable: 'لا توجد بيانات متاحة',
    pullToRefresh: 'اسحب للأسفل للتحديث أو اضغط على الزر أدناه',
    entityType: 'نوع المؤسسة',
    alternativeName: 'اسم بديل للمؤسسة (لسهولة البحث)',
    phoneNumber: 'رقم الهاتف',
    secondPhoneNumber: 'رقم الهاتف الثاني (اختياري)',
    address: 'العنوان',
    website: 'الموقع الإلكتروني',
    socialMedia: 'وسائل التواصل الاجتماعي',
    subSpecialty: 'التخصص الفرعي',
    isOwner: 'هل أنت مسؤول / مالك المؤسسة؟',
    yes: 'نعم',
    no: 'لا',
    save: 'حفظ',
    next: 'التالي',
    previous: 'السابق',
    cancel: 'إلغاء',
  },

  myClients: {
    title: 'موكلي',
    selectRole: 'اختر الدور',
    clientList: 'قائمة الموكلين',
    clientDetails: 'تفاصيل الموكل',
    clientName: 'اسم الموكل',
    phoneNumber: 'رقم الهاتف',
    email: 'البريد الإلكتروني',
    address: 'العنوان',
    noClients: 'لا يوجد موكلون',
    noRoleSelected: 'لم يتم اختيار دور',
    addNewClient: 'إضافة عميل',
    addNewClientTitle: 'إضافة موكل جديد',
    step1Title: 'الخطوة 1 من 2 - البحث عن الموكل',
    step1Description: 'ابحث عن الموكل برقم الهاتف المحمول',
    step2Title: 'الخطوة 2 من 2 - البيانات الأساسية',
    step2Description: 'أدخل بيانات الموكل الأساسية',
    searchByPhone: 'البحث برقم الهاتف المحمول',
    phonePlaceholder: 'أدخل رقم الهاتف المحمول',
    searchButton: 'بحث',
    searching: 'جاري البحث...',
    clientFound: 'تم العثور على الموكل',
    clientFoundMessage: 'تم العثور على الموكل: {name}',
    addToOffice: 'إضافة لجهة العمل',
    adding: 'جاري الإضافة...',
    clientAlreadyAssociated: 'الموكل مرتبط بالفعل',
    clientAlreadyAssociatedMessage: 'هذا الموكل مرتبط بالفعل بجهة العمل المحددة',
    tryAnotherNumber: 'جرب رقم آخر',
    noClientFound: 'لم يتم العثور على موكل',
    noClientFoundMessage: 'لا يوجد موكل بهذا الرقم: {phone}',
    createNewClient: 'إنشاء موكل جديد',
    tryAgain: 'جرب مرة أخرى',
    fullName: 'الإسم الموكل بالكامل',
    fullNamePlaceholder: 'أدخل الاسم الكامل',
    mobileNumber: 'رقم الهاتف المحمول',
    mobileNumberPlaceholder: '01234567870',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'example@email.com',
    birthDate: 'تاريخ الميلاد',
    birthDatePlaceholder: 'تاريخ الميلاد (مثال: 05-11-1985)',
    selectedDate: 'التاريخ المحدد',
    gender: 'النوع',
    male: 'ذكر',
    female: 'أنثى',
    creating: 'جاري الإنشاء...',
    next: 'التالي',
    editClient: 'تعديل بيانات الموكل',
    editClientTitle: 'تحديث معلومات {name}',
    updateClient: 'تحديث',
    updating: 'جاري التحديث...',
    maritalStatus: 'الحالة الاجتماعية',
    selectMaritalStatus: 'اختر الحالة الاجتماعية',
    single: 'أعزب',
    married: 'متزوج',
    divorced: 'مطلق',
    widowed: 'أرمل/أرملة',
    nationalID: 'الرقم القومي',
    nationalIDPlaceholder: 'أدخل الرقم القومي',
    secondaryPhone: 'رقم هاتف إضافي',
    secondaryPhonePlaceholder: 'رقم هاتف إضافي',
    saveChanges: 'حفظ التغييرات',
  },

  legalFiles: {
    title: 'الملفات القانونية',
    subtitle: 'إدارة المستندات القانونية وملفات القضايا',
    selectRole: 'اختر الدور',
    selectClient: 'اختر الموكل',
    legalFilesList: 'قائمة الملفات القانونية',
    fileNumber: 'رقم الملف',
    fileTitle: 'عنوان الملف',
    fileType: 'نوع الملف',
    status: 'الحالة',
    createdDate: 'تاريخ الإنشاء',
    lastUpdated: 'آخر تحديث',
    overview: 'نظرة عامة',
    agenda: 'الأجندة',
    attachments: 'المرفقات',
    noFiles: 'لا توجد ملفات',
    noClientSelected: 'لم يتم اختيار موكل',
    noRoleSelected: 'لم يتم اختيار دور',
    addNewFile: 'إضافة ملف جديد',
    addNewFileTitle: 'إضافة ملف قانوني جديد',
    addNewFileSubtitle: 'إنشاء ملف قانوني جديد لـ {clientName}',
    basicInformation: 'المعلومات الأساسية',
    caseInformation: 'معلومات القضية',
    financialInformation: 'المعلومات المالية',
    additionalNotes: 'ملاحظات إضافية',
    fileTitleLabel: 'عنوان الملف',
    fileTitlePlaceholder: 'أدخل عنوان الملف',
    fileTypeLabel: 'نوع الملف',
    selectFileType: 'اختر نوع الملف',
    statusLabel: 'الحالة',
    selectStatus: 'اختر الحالة',
    caseNumberLabel: 'رقم القضية',
    caseNumberPlaceholder: 'أدخل رقم القضية',
    descriptionLabel: 'الوصف',
    descriptionPlaceholder: 'أدخل وصف الملف',
    caseYearLabel: 'سنة القضية',
    caseYearPlaceholder: '2024',
    courtTypeLabel: 'جهة القيد / المحكمة',
    selectCourtType: 'اختر نوع المحكمة',
    caseAreaLabel: 'مجال القضية',
    caseAreaPlaceholder: 'مثل: مدني، جنائي، تجاري',
    firstSessionDateLabel: 'تاريخ الجلسة الأولى',
    caseOpponentsLabel: 'الخصوم في القضية',
    caseOpponentsPlaceholder: 'أدخل أسماء الخصوم',
    expectedFeesLabel: 'الأتعاب المتوقعة',
    totalIncomeLabel: 'إجمالي الدخل (جنيه)',
    totalExpensesLabel: 'إجمالي المصروفات (جنيه)',
    notesLabel: 'ملاحظات',
    notesPlaceholder: 'أدخل أي ملاحظات أو تعليقات إضافية',
    caseTypeLabel: 'نوع القضية',
    selectCaseType: 'اختر نوع القضية',
    mainSubjectLabel: 'الموضوع الرئيسي',
    mainSubjectPlaceholder: 'أدخل الموضوع الرئيسي للقضية',
    createFile: 'إنشاء الملف',
    creating: 'جاري الإنشاء...',
    fileTitleRequired: 'عنوان الملف مطلوب',
    descriptionRequired: 'وصف الملف مطلوب',
    caseNumberRequired: 'رقم القضية مطلوب',
    fileTypeRequired: 'نوع الملف مطلوب',
    statusRequired: 'حالة الملف مطلوبة',
    selectRoleClientFirst: 'يرجى اختيار الدور والموكل أولاً',
    editFile: 'تعديل الملف',
    archiveFile: 'أرشفة الملف',
    unarchiveFile: 'استعادة الملف من الأرشيف',
    editFileTitle: 'تعديل الملف القانوني',
    archiveFileTitle: 'أرشفة الملف',
    unarchiveFileTitle: 'استعادة الملف من الأرشيف',
    archiveFileMessage: 'هل أنت متأكد من أنك تريد أرشفة هذا الملف؟ سيتم نقل الملف إلى الأرشيف.',
    unarchiveFileMessage: 'هل أنت متأكد من أنك تريد استعادة هذا الملف من الأرشيف؟ سيتم إرجاع الملف إلى القائمة الرئيسية.',
    confirmArchive: 'تأكيد الأرشفة',
    confirmUnarchive: 'تأكيد الاستعادة',
    cancelArchive: 'إلغاء',
    cancelEdit: 'إلغاء التعديل',
    saveFile: 'حفظ الملف',
    saving: 'جاري الحفظ...',
  },

  // Community
  community: {
    title: 'المجتمع',
    subtitle: 'انضم إلى مجتمع المحامين والمحاميات، شارك الخبرات، وابني شبكة مهنية قوية',
    searchPlaceholder: 'ابحث في المجتمع...',
    forums: 'المنتديات',
    events: 'الفعاليات',
    network: 'الشبكة المهنية',
    forumsDescription: 'مناقشات قانونية',
    eventsDescription: 'ندوات وورش عمل',
    networkDescription: 'تواصل مع المحامين',
    // Forums
    createForum: 'إنشاء منتدى',
    allCategories: 'جميع الفئات',
    noForums: 'لا توجد منتديات',
    noForumsSearch: 'لم نجد منتديات تطابق بحثك',
    createFirstForum: 'ابدأ بإنشاء أول منتدى',
    discussionForums: 'منتديات النقاش',
    participateInDiscussions: 'شارك في النقاشات القانونية واطلب المشورة من الزملاء',
    // Events
    createEvent: 'إنشاء فعالية',
    allTypes: 'جميع الأنواع',
    allTime: 'جميع الأوقات',
    upcoming: 'قادمة',
    today: 'اليوم',
    thisWeek: 'هذا الأسبوع',
    noEvents: 'لا توجد فعاليات',
    noEventsSearch: 'لم نجد فعاليات تطابق بحثك',
    createFirstEvent: 'ابدأ بإنشاء أول فعالية',
    professionalEvents: 'فعاليات مهنية',
    attendSeminars: 'احضر الندوات وورش العمل واللقاءات المهنية',
    // Network
    allSpecialties: 'جميع التخصصات',
    allExperience: 'جميع الخبرات',
    connectedOnly: 'المتصلين فقط',
    lawyers: 'محامين',
    noLawyers: 'لا يوجد محامين',
    noLawyersSearch: 'لم نجد محامين يطابقون بحثك',
    noLawyersNetwork: 'لا يوجد محامين في الشبكة حالياً',
    professionalNetwork: 'شبكة مهنية',
    buildRelationships: 'ابني شبكة من العلاقات المهنية مع المحامين الآخرين',
    // Common
    viewDetails: 'عرض التفاصيل',
    viewProfile: 'عرض الملف الشخصي',
    connect: 'تواصل',
    connected: 'متصل',
    pending: 'في الانتظار',
    declined: 'مرفوض',
    rsvp: 'تسجيل',
    yearsExperience: 'سنة خبرة',
    languages: 'اللغات',
    // Modals
    createNewForum: 'إنشاء منتدى جديد',
    createNewEvent: 'إنشاء فعالية جديدة',
    underDevelopment: 'هذه الميزة قيد التطوير',
    close: 'إغلاق',
    // Forum Creation
    rules: 'قواعد المنتدى',
    isPublic: 'منتدى عام',
    allowThreadCreation: 'السماح بإنشاء المواضيع',
    requireApproval: 'يتطلب موافقة للمواضيع',
    color: 'اللون',
    icon: 'الأيقونة',
    // Thread Types
    discussion: 'نقاش',
    question: 'سؤال',
    announcement: 'إعلان',
    poll: 'استطلاع',
    // Stats
    posts: 'منشورات',
    comments: 'تعليقات',
    attendees: 'مشاركين',
    connections: 'اتصالات',
    rating: 'تقييم',
    reviews: 'مراجعات',
    // Time
    lastPost: 'آخر منشور',
    registeredAt: 'مسجل في',
    // Status
    published: 'منشور',
    cancelled: 'ملغي',
    completed: 'مكتمل',
    draft: 'مسودة',
    // Location
    onlineEvent: 'فعالية أونلاين',
    link: 'رابط',
    organizedBy: 'منظم:',
    // Experience ranges
    experience0to2: '0-2 سنوات',
    experience3to5: '3-5 سنوات',
    experience6to10: '6-10 سنوات',
    experience10plus: '10+ سنوات',
  },

  events: {
    title: 'الأجندة',
    calendarView: 'عرض التقويم',
    listView: 'عرض القائمة',
    today: 'اليوم',
    noEvents: 'لا توجد أحداث',
    createNewEvent: 'إنشاء حدث جديد',
    editEvent: 'تعديل الحدث',
    deleteEvent: 'حذف الحدث',
    addNewEvent: 'إضافة حدث جديد',
    selectOffice: 'اختر المؤسسة',
    selectClient: 'اختر العميل',
    selectLegalFile: 'اختر الملف القانوني',
    fileTitle: 'عنوان الملف',
    enterFileTitle: 'أدخل عنوان الملف',
    eventType: 'نوع الحدث',
    eventTitle: 'عنوان الحدث',
    eventDate: 'تاريخ الحدث',
    eventTime: 'وقت الحدث',
    eventLocation: 'مكان الحدث',
    eventDescription: 'وصف الحدث',
    previousMonth: 'الشهر السابق',
    nextMonth: 'الشهر التالي',
    previousMonthArrow: 'السابق',
    nextMonthArrow: 'التالي',
    // Event types
    eventTypes: {
      clientMeeting: 'اجتماع مع الموكل',
      courtSession: 'جلسة محكمة',
      governmental: 'إجراء حكومي',
    },
    // Event statuses
    eventStatuses: {
      upcoming: 'قادم',
      inProgress: 'جاري',
      completed: 'مكتمل',
      cancelled: 'ملغي',
      expired: 'منتهي الصلاحية',
    },
    // Place types
    placeTypes: {
      office: 'جهة العمل',
      court: 'المحكمة',
      clientLocation: 'موقع الموكل',
      government: 'جهة حكومية',
      other: 'أخرى',
    },
    // Calendar localization
    daysOfWeek: {
      sunday: 'الأحد',
      monday: 'الاثنين',
      tuesday: 'الثلاثاء',
      wednesday: 'الأربعاء',
      thursday: 'الخميس',
      friday: 'الجمعة',
      saturday: 'السبت',
    },
    daysOfWeekShort: {
      sunday: 'أحد',
      monday: 'اثن',
      tuesday: 'ثلث',
      wednesday: 'أرب',
      thursday: 'خمس',
      friday: 'جمع',
      saturday: 'سبت',
    },
    months: {
      january: 'يناير',
      february: 'فبراير',
      march: 'مارس',
      april: 'أبريل',
      may: 'مايو',
      june: 'يونيو',
      july: 'يوليو',
      august: 'أغسطس',
      september: 'سبتمبر',
      october: 'أكتوبر',
      november: 'نوفمبر',
      december: 'ديسمبر',
    },
    monthsShort: {
      january: 'يناير',
      february: 'فبراير',
      march: 'مارس',
      april: 'أبريل',
      may: 'مايو',
      june: 'يونيو',
      july: 'يوليو',
      august: 'أغسطس',
      september: 'سبتمبر',
      october: 'أكتوبر',
      november: 'نوفمبر',
      december: 'ديسمبر',
    },
  },


  support: {
    title: 'مركز الدعم',
    aboutMorafaa: 'حول نظام مرافعة',
    aboutDescription: 'نظام مرافعة هو منصة شاملة لإدارة الممارسة القانونية مصممة خصيصاً للمحامين والمهنيين القانونيين. يبسط نظامنا إدارة القضايا وعلاقات العملاء والتعامل مع المستندات والتتبع المالي، مما يمكنك من التركيز على ما يهم أكثر - تقديم خدمات قانونية استثنائية لعملائك. مع إدارة التقويم المتكاملة والفواتير الآلية والتخزين الآمن للمستندات، تمكن مرافعة المهنيين القانونيين من بناء ممارسات أكثر كفاءة ونجاحاً.',
    connectWithUs: 'تواصل معنا',
    facebook: 'تابعنا على فيسبوك',
    instagram: 'تابعنا على إنستغرام',
    customerSupport: 'دعم العملاء',
    whatsappSupport: 'دعم واتساب',
    legalInformation: 'المعلومات القانونية',
    termsConditions: 'الشروط والأحكام',
    privacyPolicy: 'سياسة الخصوصية',
    viewTerms: 'عرض الشروط والأحكام',
    viewPrivacy: 'عرض سياسة الخصوصية',
    english: 'الإنجليزية',
    arabic: 'العربية',
    contactUs: 'اتصل بنا',
    inquiryType: 'النوع',
    selectType: 'اختر النوع',
    message: 'الرسالة',
    messagePlaceholder: 'اكتب رسالتك هنا...',
    send: 'إرسال',
    sending: 'جاري الإرسال...',
    inquirySent: 'تم إرسال الاستفسار',
    inquirySentMessage: 'تم إرسال استفسارك بنجاح. سوف نتصل بك قريباً.',
    errorSending: 'خطأ في الإرسال',
    errorSendingMessage: 'حدث خطأ أثناء إرسال استفسارك. يرجى المحاولة مرة أخرى.',
  },

  referral: {
    title: 'برنامج الإحالة',
    subtitle: 'رشّح محامياً واحصل على شهر بدون إعلانات',
    headerDescription: 'شارك مرافعة مع زملائك المحامين واكسب شهر Ad-Free لكل إحالة ناجحة',
    howItWorks: 'كيف يعمل البرنامج',
    step1: 'شارك كود الدعوة أو الرابط مع زملائك المحامين.',
    step2: 'يسجّلون بكودك ويكملون أول جلسة دخول خلال 7 أيام.',
    step3: 'تحصل على شهر بدون إعلانات (Ad-Free) لكل إحالة ناجحة — حتى 10 شهرياً.',
    yourCode: 'كود الدعوة الخاص بك',
    yourInviteLink: 'رابط الدعوة',
    copy: 'انسخ الكود',
    copyLink: 'انسخ الرابط',
    share: 'شارك',
    copied: 'تم النسخ',
    totalReferrals: 'الإحالات الناجحة',
    successfulReferrals: 'زملاء سجّلوا ودخلوا لأول مرة',
    freeMonthsEarned: 'أشهر Ad-Free المكتسبة',
    monthsOfFreeService: 'شهور بدون إعلانات',
    termsTitle: 'الشروط والأحكام:',
    terms: [
      'تُحتسب الإحالة الناجحة بعد تسجيل المُحال بكودك وإكمال أول جلسة خلال 7 أيام',
      'تُضاف مكافأة شهر Ad-Free واحدة إلى حسابك (مصدر: إحالة)',
      'حد أقصى 10 إحالات مكافأة لكل شهر تقويمي لكل محيل',
      'لا تُحتسب إحالة نفس الحساب أو الإحالات المكررة',
      'أكواد الإحالة شخصية — تجنّب نشرها علناً بشكل عشوائي',
      'تحتفظ مرافعة بالحق في تعديل أو إيقاف البرنامج'
    ],
    shareMessage: 'مرحباً!\n\nأدعوك للانضمام إلى مرافعة.\n\nعن مرافعة: منصة مصرية لإدارة الممارسة القانونية — موكلون، ملفات، أجندة، مكتبة قانونية، ودليل المحامي، من التطبيق أو الويب.\n\nكود الدعوة:',
    shareTitle: 'انضم إلى مرافعة',
    statsEmpty: 'لا توجد إحالات ناجحة بعد',
    statsError: 'تعذر تحميل الأرقام. اضغط تحديث.',
    statsRefresh: 'تحديث',
  },


  teamMembers: {
    title: 'أعضاء الفريق',
    selectOffice: 'اختر جهة العمل',
    teamMembersList: 'قائمة أعضاء الفريق',
    memberDetails: 'تفاصيل العضو',
    memberName: 'اسم العضو',
    memberEmail: 'البريد الإلكتروني',
    memberPhone: 'رقم الهاتف',
    memberRole: 'دور العضو',
    permissions: 'الصلاحيات',
    accessClients: 'الوصول للموكلين',
    accessLegalFiles: 'الوصول للملفات القانونية',
    accessEvents: 'الوصول للأحداث',
    noMembers: 'لا يوجد أعضاء فريق',
    noOfficeSelected: 'لم يتم اختيار جهة عمل',
    addMember: 'إضافة عضو',
    editMember: 'تعديل العضو',
    removeMember: 'إزالة العضو',
    updatePermissions: 'تحديث الصلاحيات',
    permissionsUpdated: 'تم تحديث الصلاحيات بنجاح!',
    memberAdded: 'تم إضافة العضو بنجاح!',
    memberRemoved: 'تم إزالة العضو بنجاح!',
    errorUpdatingPermissions: 'فشل في تحديث الصلاحيات. يرجى المحاولة مرة أخرى.',
    errorAddingMember: 'فشل في إضافة العضو. يرجى المحاولة مرة أخرى.',
    errorRemovingMember: 'فشل في إزالة العضو. يرجى المحاولة مرة أخرى.',
  },

  lawyerGuide: {
    title: 'دليل المحامي',
    subtitle: 'وصول سريع للموارد والأدوات القانونية الأساسية',
    categories: 'الفئات',
    resources: 'الموارد',
    searchPlaceholder: 'البحث في الفئات والموارد...',
    noCategories: 'لا توجد فئات متاحة',
    noResources: 'لا توجد موارد متاحة',
    selectCategory: 'اختر فئة لعرض الموارد',
    noResults: 'لم يتم العثور على نتائج',
    openInNewTab: 'فتح في تبويب جديد',
    externalLink: 'رابط خارجي',
    categoryDescription: 'وصف الفئة',
    resourceDescription: 'وصف المورد',
    loadingCategories: 'جاري تحميل الفئات...',
    loadingResources: 'جاري تحميل الموارد...',
    errorLoadingCategories: 'فشل في تحميل الفئات',
    errorLoadingResources: 'فشل في تحميل الموارد',
    searchFailed: 'فشل في البحث',
  },

  subscriptions: {
    title: 'الاشتراكات',
    subtitle: 'إدارة اشتراكاتك وخطط الدفع للحصول على أفضل تجربة في مرافعة',
    currentSubscription: 'اشتراكك الحالي',
    usageDashboard: 'لوحة تحكم الاستخدام',
    planComparison: 'مقارنة الخطط',
    noActiveSubscription: 'لا يوجد اشتراك نشط',
    noActiveSubscriptionMessage: 'اختر خطة اشتراك لبدء استخدام جميع المميزات',
    viewPlans: 'عرض الخطط',
    manageSubscription: 'إدارة الاشتراك',
    refreshData: 'تحديث البيانات',
    quickActions: 'الإجراءات السريعة',
    upgradePlan: 'ترقية الخطة',
    choosePlan: 'اختر الخطة',
    currentPlan: 'خطتك الحالية',
    mostPopular: 'الأكثر شعبية',
    freeTrial: 'تجربة مجانية',
    dayTrial: 'يوم تجريبي',
    daysLeft: 'أيام متبقية',
    trialDaysLeft: 'أيام التجربة المتبقية',
    startDate: 'تاريخ البدء',
    endDate: 'تاريخ الانتهاء',
    autoRenew: 'التجديد التلقائي',
    yes: 'نعم',
    no: 'لا',
    active: 'نشط',
    trial: 'تجريبي',
    expired: 'منتهي',
    cancelled: 'ملغي',
    pastDue: 'متأخر',
    loading: 'جاري التحميل...',
    loadingSubscription: 'جاري تحميل بيانات الاشتراك...',
    loadingUsageStats: 'جاري تحميل إحصائيات الاستخدام...',
    failedToLoad: 'فشل في تحميل بيانات الاشتراك',
    tryAgain: 'إعادة المحاولة',
    close: 'إغلاق',
    // Plan features
    planFeatures: 'مميزات الخطة',
    offices: 'المكاتب',
    clients: 'العملاء',
    files: 'الملفات',
    unlimited: 'غير محدود',
    perOffice: 'لكل مكتب',
    perClient: 'لكل عميل',
    eventReminders: 'تذكيرات الأحداث',
    advancedStats: 'إحصائيات متقدمة',
    communityAccess: 'الوصول إلى المجتمع',
    phoneSupport: 'دعم هاتفي',
    teamMembers: 'أعضاء الفريق',
    perMember: 'لكل عضو',
    // Usage warnings
    usageWarnings: 'تنبيهات الاستخدام',
    approachingLimit: 'تقترب من الحد الأقصى',
    // Plan names
    freePlan: 'الخطة المجانية',
    basicPlan: 'الخطة الأساسية',
    professionalPlan: 'الخطة الاحترافية',
    // Plan descriptions
    freePlanDescription: 'مثالي للمحامين الأفراد في بداية مسيرتهم.',
    basicPlanDescription: 'مثالية لمكاتب المحاماة النامية',
    professionalPlanDescription: 'ميزات شاملة لشركات المحاماة الكبرى.',
  },

  offices: {
    title: 'إدارة جهات العمل',
    subtitle: 'إدارة وتتبع جميع جهات العمل والفروع القانونية',
    loading: 'جاري تحميل جهات العمل...',
    loadingOffices: 'جاري تحميل جهات العمل...',
    errorTitle: 'خطأ في تحميل جهات العمل',
    loadError: 'فشل في تحميل جهات العمل من قاعدة البيانات. يرجى المحاولة مرة أخرى.',
    demoMode: 'وضع العرض التوضيحي: عرض بيانات تجريبية بسبب فشل تحميل قاعدة البيانات. انقر على "إعادة تحميل" للمحاولة مرة أخرى.',
    exportExcel: 'تصدير إكسل',
    refreshing: 'جاري التحديث...',
    refresh: 'تحديث',
    allStatus: 'جميع الحالات',
    activeStatus: 'نشط',
    archived: 'مؤرشف',
    deleted: 'محذوف',
    noOffices: 'لا توجد جهات عمل',
    noOfficesFound: 'لم يتم العثور على جهات عمل',
    tryAdjusting: 'حاول تعديل معايير البحث أو التصفية',
    noOfficesCreated: 'لم يتم إنشاء أي جهات عمل بعد',
    officeDetails: 'تفاصيل جهة العمل',
    contactInformation: 'معلومات الاتصال',
    address: 'العنوان',
    phone: 'الهاتف',
    email: 'البريد الإلكتروني',
    website: 'الموقع الإلكتروني',
    statusDates: 'الحالة والتواريخ',
    status: 'الحالة',
    created: 'تاريخ الإنشاء',
    updated: 'تاريخ التحديث',
    officeStaff: 'موظفو جهة العمل',
    loadingStaff: 'جاري تحميل الموظفين...',
    retry: 'إعادة المحاولة',
    noStaffMembers: 'لا يوجد موظفون',
    noStaffAssigned: 'لم يتم تعيين أي موظفين لهذه جهة العمل بعد',
    inactive: 'غير نشط',
    activeStaff: 'نشط',
    noPhone: 'لا يوجد هاتف',
    noEmail: 'لا يوجد بريد إلكتروني',
    officeName: 'اسم جهة العمل',
    alternativeName: 'الاسم البديل',
    description: 'الوصف',
    optionalEmail: 'البريد الإلكتروني (اختياري)',
    officeDescription: 'وصف جهة العمل',
    logoURL: 'رابط الشعار',
    briefDescription: 'وصف مختصر لجهة العمل...',
    logoPlaceholder: 'https://example.com/logo.png',
    searchPlaceholder: 'البحث في جهات العمل...',
    failedToLoadUsers: 'فشل في تحميل موظفي جهة العمل',
    // Add Office Modal
    searching: 'جاري البحث...',
    selectSubSpecialty: 'اختر التخصص الفرعي',
    lawyerTitle: 'لقب المحامي',
    salaryConfiguration: 'إعدادات الراتب',
    salaryType: 'نوع الراتب',
    studentIntern: 'طالب/متدرب (بدون راتب)',
    fixedSalary: 'راتب ثابت',
    percentageBased: 'نسبة مئوية',
    fixedAndPercentage: 'راتب ثابت + نسبة مئوية',
    fixedSalaryEGP: 'الراتب الثابت (جنيه مصري)',
    profitPercentage: 'نسبة الربح (%)',
    officeNameRequired: 'اسم جهة العمل مطلوب',
    phoneNumberRequired: 'رقم الهاتف مطلوب',
    validEmailAddress: 'يرجى إدخال عنوان بريد إلكتروني صحيح',
    validSalaryAmount: 'يرجى إدخال مبلغ راتب صحيح',
    validProfitPercentage: 'يرجى إدخال نسبة ربح صحيحة',
    // Sub-specialty options
    personalStatus: 'قانون الأحوال الشخصية',
    commercial: 'القانون التجاري',
    criminal: 'القانون الجنائي',
    civil: 'القانون المدني',
    administrative: 'القانون الإداري',
    labor: 'قانون العمل',
    tax: 'قانون الضرائب',
    realEstate: 'قانون العقارات',
    intellectualProperty: 'قانون الملكية الفكرية',
    family: 'قانون الأسرة',
  },
};

// English strings
export const stringsEn: LocalizedStrings = {
  common: {
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    copy: 'Copy',
    share: 'Share',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    search: 'Search',
    filter: 'Filter',
    refresh: 'Refresh',
    view: 'View',
    details: 'Details',
    select: 'Select',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    required: 'This field is required',
    title: 'Title',
    description: 'Description',
    tags: 'Tags',
    settings: 'Settings',
  },

  // Login
  login: {
    title: 'Login',
    welcomeBack: 'Welcome Back',
    subtitle: 'Sign in to your Morafaa account to continue',
    signIn: 'Sign In',
    signInSubtitle: 'Enter your credentials to access your account',
    email: 'Email Address',
    emailPlaceholder: 'Enter your email address',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    forgotPassword: 'Forgot password?',
    signingIn: 'Signing in...',
    resetPassword: 'Reset Password',
    resetPasswordSubtitle: 'Enter your email address and we\'ll send you a link to reset your password.',
    sendResetEmail: 'Send Reset Email',
    sending: 'Sending...',
    noAccount: 'Don\'t have an account?',
    createAccount: 'Create Account',
    copyright: '© 2025 Morafaa. All rights reserved.',
    language: 'Language',
  },

  // Sign Up
  signup: {
    title: 'Create Account',
    welcome: 'Welcome',
    subtitle: 'Create your Morafaa account to get started',
    createAccount: 'Create Account',
    createAccountSubtitle: 'Enter your information to create a new account',
    firstName: 'First Name',
    firstNamePlaceholder: 'Enter your first name',
    lastName: 'Last Name',
    lastNamePlaceholder: 'Enter your last name',
    email: 'Email Address',
    emailPlaceholder: 'Enter your email address',
    phone: 'Phone Number',
    phonePlaceholder: 'Enter your phone number',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Re-enter your password',
    creatingAccount: 'Creating Account...',
    haveAccount: 'Already have an account?',
    signIn: 'Sign In',
    copyright: '© 2025 Morafaa. All rights reserved.',
    language: 'Language',
    passwordMismatch: 'Passwords do not match',
    weakPassword: 'Password is too weak',
    emailInUse: 'Email address is already in use',
    invalidEmail: 'Invalid email address',
    required: 'This field is required',
  },

  search: {
    general: 'Search...',
    users: 'Search by user ID, email, name, or phone...',
    offices: 'Search by office ID, name, phone, or email...',
    clients: 'Search by client ID, name, email, or phone...',
    legalFiles: 'Search by file ID, title, or lawyer name...',
    events: 'Search by event ID or lawyer name...',
    logs: 'Search by log level, category, or message...',
    presetData: 'Search by preset data...',
  },

  navigation: {
    dashboard: 'Advanced Statistics',
    agenda: 'Agenda',
    myOffices: 'My Offices',
    myClients: 'My Clients',
    legalFiles: 'Legal Files',
    teamMembers: 'Team Members',
    lawyerGuide: 'Lawyer Guide',
    statistics: 'Statistics',
    community: 'Community',
    subscriptions: 'Subscriptions',
    myProfile: 'My Profile',
    referralProgram: 'Referral Program',
    support: 'Support Center',
  },

  dashboard: {
    title: 'Enhanced Analytics Dashboard',
    welcome: 'Welcome to your Morafaa Legal Practice Management',
    loading: 'Loading Dashboard',
    loadingRoles: 'Loading your roles and offices...',
    loadingStats: 'Fetching real-time statistics...',
    errorTitle: 'Error Loading Dashboard',
    loadError: 'Failed to load dashboard statistics. Please try again.',
    lastUpdated: 'Last Updated',
    updating: 'Updating dashboard statistics...',
    myClients: 'My Clients',
    myOffices: 'My Offices',
    upcomingEvents: 'Upcoming Events',
    legalFiles: 'Legal Files',
    recentActivity: 'Recent Activity',
    live: 'Live',
    active: 'Active',
    next30Days: 'Next 30 Days',
    documents: 'Documents',
    transactions: 'Transactions',
    last7Days: 'Last 7 Days',
    // Enhanced Dashboard
    noData: 'No data available',
    refresh: 'Refresh',
    // Time Range Selector
    '7d': '7 Days',
    '30d': '30 Days',
    '90d': '90 Days',
    '1y': '1 Year',
    // Financial Overview
    financialOverview: 'Financial Overview',
    totalRevenue: 'Total Revenue',
    totalExpenses: 'Total Expenses',
    netProfit: 'Net Profit',
    revenue: 'Revenue',
    expenses: 'Expenses',
    profit: 'Profit',
    revenueExpensesProfitTrend: 'Revenue, Expenses & Profit Trend',
    outstandingInvoices: 'Outstanding Invoices',
    overdueInvoices: 'Overdue Invoices',
    // Client Analytics
    clientAnalytics: 'Client Analytics',
    acquisitionFunnel: 'Client Acquisition Funnel',
    clientValueDistribution: 'Client Value Distribution',
    clientGrowthRate: 'Client Growth Rate',
    newClientsLast30Days: 'New Clients Last 30 Days',
    clients: 'Clients',
    count: 'Count',
    // Case Performance
    casePerformance: 'Case Performance',
    caseStatusDistribution: 'Case Status Distribution',
    winRate: 'Win Rate',
    averageCaseDuration: 'Average Case Duration',
    days: 'Days',
    // Office Performance
    officePerformance: 'Office Performance',
    files: 'Files',
    // Predictive Analytics
    predictiveAnalytics: 'Predictive Analytics',
    predictedRevenueNext3Months: 'Predicted Revenue Next 3 Months',
    noPredictionData: 'No prediction data available',
    // Chart Labels
    increase: 'increase',
    decrease: 'decrease',
    noChange: 'no change',
    // Status Labels
    statusActive: 'Active',
    statusPending: 'Pending',
    statusClosed: 'Closed',
    statusWon: 'Won',
    statusLost: 'Lost',
    // Value Ranges
    highValue: 'High Value',
    mediumValue: 'Medium Value',
    lowValue: 'Low Value',
    // Data Availability
    dataNotice: 'Data Availability Notice',
    dataNoticeDescription: 'Statistics and charts are displayed based on actual data available in the database. Some metrics may not be available currently.',
    totalCases: 'Total Cases',
    activeCases: 'Active Cases',
  },


  profile: {
    title: 'My Profile',
    personalInfo: 'Personal Information',
    firstName: 'First Name',
    middleName: 'Middle Name',
    lastName: 'Last Name',
    email: 'Email',
    phoneNumber: 'Phone Number',
    phoneNumber2: 'Phone Number 2',
    nationalID: 'National ID',
    birthdate: 'Birth Date',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    address: 'Address',
    photo: 'Photo',
    uploadPhoto: 'Upload Photo',
    saveChanges: 'Save Changes',
    cancelChanges: 'Cancel Changes',
    editProfile: 'Edit Profile',
    profileUpdated: 'Profile updated successfully!',
    updateError: 'Failed to update profile. Please try again.',
  },

  myOffices: {
    title: 'My Offices',
    selectOffice: 'Select Office',
    selectOfficeTitle: 'Select an Office',
    selectOfficeDescription: 'Choose an office from the list to view details and your roles',
    officeDetails: 'Office Details',
    officeName: 'Office Name',
    roleName: 'Role Name',
    roleDescription: 'Role Description',
    permissions: 'Permissions',
    noOffices: 'No offices found',
    addOffice: 'Add Office',
    addNewOffice: 'Add New Legal Entity',
    step1Title: 'Search for Entity',
    step1Description: 'Search for the entity\'s file if it already exists on the Morafaa system',
    step2Title: 'Basic Entity Information',
    step2Description: 'Fill in the basic information for the legal entity',
    step3Title: 'Edit Role',
    // Office Details Panel
    officeInformation: 'Office Information',
    officeAddress: 'Address',
    officePhone: 'Phone',
    officeEmail: 'Email',
    officeWebsite: 'Website',
    yourRoles: 'Your Roles in This Office',
    roles: 'role',
    rolesPlural: 'roles',
    joined: 'Joined',
    creator: 'Creator',
    admin: 'Admin',
    salaryType: 'Salary Type',
    salaryValue: 'Salary Value',
    profitPercentage: 'Profit %',
    specialty: 'Specialty',
    status: 'Status',
    studentIntern: 'Student/Intern',
    fixedSalary: 'Fixed Salary',
    percentageBased: 'Percentage Based',
    fixedPercentage: 'Fixed + Percentage',
    generalPractice: 'General Practice',
    legalProfessional: 'Legal Professional',
    unknown: 'Unknown',
    archived: 'Archived',
    active: 'Active',
    // Edit Office Modal
    editOffice: 'Edit Office',
    roleDetails: 'Role Details',
    lawyerTitle: 'Lawyer Title',
    salaryValueRequired: 'Salary value is required',
    fixedSalaryAmount: 'Fixed Salary Amount',
    percentageAmount: 'Percentage Amount',
    update: 'Update',
    updating: 'Updating...',
    updateError: 'Failed to update office. Please try again.',
    canEditRole: 'You can edit your role information',
    cannotEditRole: 'You can only edit when you are the only staff member',
    actions: 'Actions',
    step3Description: 'Specify your role in this entity',
    searchPlaceholder: 'Office',
    entityNotFound: 'Legal entity not found?',
    createNewEntity: 'Create new legal entity',
    noDataAvailable: 'No data available',
    pullToRefresh: 'Pull down to refresh or press the button below',
    entityType: 'Entity Type',
    alternativeName: 'Alternative name for the entity (for easy search)',
    phoneNumber: 'Phone Number',
    secondPhoneNumber: 'Second Phone Number (optional)',
    address: 'Address',
    website: 'Website',
    socialMedia: 'Social Media',
    subSpecialty: 'Sub-specialty',
    isOwner: 'Are you responsible / owner of the institution?',
    yes: 'Yes',
    no: 'No',
    save: 'Save',
    next: 'Next',
    previous: 'Previous',
    cancel: 'Cancel',
  },

  myClients: {
    title: 'My Clients',
    selectRole: 'Select Role',
    clientList: 'Client List',
    clientDetails: 'Client Details',
    clientName: 'Client Name',
    phoneNumber: 'Phone Number',
    email: 'Email',
    address: 'Address',
    noClients: 'No clients found',
    noRoleSelected: 'No role selected',
    addNewClient: 'Add Client',
    addNewClientTitle: 'Add New Client',
    step1Title: 'Step 1 of 2 - Search for Client',
    step1Description: 'Search for client by mobile number',
    step2Title: 'Step 2 of 2 - Basic Information',
    step2Description: 'Enter client basic information',
    searchByPhone: 'Search by Mobile Number',
    phonePlaceholder: 'Enter mobile number',
    searchButton: 'Search',
    searching: 'Searching...',
    clientFound: 'Client Found',
    clientFoundMessage: 'Client found: {name}',
    addToOffice: 'Add to Office',
    adding: 'Adding...',
    clientAlreadyAssociated: 'Client Already Associated',
    clientAlreadyAssociatedMessage: 'This client is already associated with the selected office',
    tryAnotherNumber: 'Try Another Number',
    noClientFound: 'No Client Found',
    noClientFoundMessage: 'No client found with this number: {phone}',
    createNewClient: 'Create New Client',
    tryAgain: 'Try Again',
    fullName: 'Full Name',
    fullNamePlaceholder: 'Enter full name',
    mobileNumber: 'Mobile Number',
    mobileNumberPlaceholder: '01234567870',
    emailLabel: 'Email',
    emailPlaceholder: 'example@email.com',
    birthDate: 'Birth Date',
    birthDatePlaceholder: 'Birth Date (e.g., 05-11-1985)',
    selectedDate: 'Selected Date',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    creating: 'Creating...',
    next: 'Next',
    editClient: 'Edit Client',
    editClientTitle: 'Update information for {name}',
    updateClient: 'Update',
    updating: 'Updating...',
    maritalStatus: 'Marital Status',
    selectMaritalStatus: 'Select Marital Status',
    single: 'Single',
    married: 'Married',
    divorced: 'Divorced',
    widowed: 'Widowed',
    nationalID: 'National ID',
    nationalIDPlaceholder: 'Enter National ID',
    secondaryPhone: 'Secondary Phone',
    secondaryPhonePlaceholder: 'Secondary phone number',
    saveChanges: 'Save Changes',
  },

  legalFiles: {
    title: 'Legal Files',
    subtitle: 'Manage your legal documents and case files',
    selectRole: 'Select Role',
    selectClient: 'Select Client',
    legalFilesList: 'Legal Files List',
    fileNumber: 'File Number',
    fileTitle: 'File Title',
    fileType: 'File Type',
    status: 'Status',
    createdDate: 'Created Date',
    lastUpdated: 'Last Updated',
    overview: 'Overview',
    agenda: 'Agenda',
    attachments: 'Attachments',
    noFiles: 'No files found',
    noClientSelected: 'No client selected',
    noRoleSelected: 'No role selected',
    addNewFile: 'Add New File',
    addNewFileTitle: 'Add New Legal File',
    addNewFileSubtitle: 'Create a new legal file for {clientName}',
    basicInformation: 'Basic Information',
    caseInformation: 'Case Information',
    financialInformation: 'Financial Information',
    additionalNotes: 'Additional Notes',
    fileTitleLabel: 'File Title',
    fileTitlePlaceholder: 'Enter file title',
    fileTypeLabel: 'File Type',
    selectFileType: 'Select File Type',
    statusLabel: 'Status',
    selectStatus: 'Select Status',
    caseNumberLabel: 'Case Number',
    caseNumberPlaceholder: 'Enter case number',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter file description',
    caseYearLabel: 'Case Year',
    caseYearPlaceholder: '2024',
    courtTypeLabel: 'Registration Authority / Court',
    selectCourtType: 'Select Court Type',
    caseAreaLabel: 'Case Area',
    caseAreaPlaceholder: 'e.g., Civil, Criminal, Commercial',
    firstSessionDateLabel: 'First Session Date',
    caseOpponentsLabel: 'Opponents in the Case',
    caseOpponentsPlaceholder: 'Enter opponent names',
    expectedFeesLabel: 'Expected Fees',
    totalIncomeLabel: 'Total Income (EGP)',
    totalExpensesLabel: 'Total Expenses (EGP)',
    notesLabel: 'Notes',
    notesPlaceholder: 'Enter any additional notes or comments',
    caseTypeLabel: 'Case Type',
    selectCaseType: 'Select Case Type',
    mainSubjectLabel: 'Main Subject',
    mainSubjectPlaceholder: 'Enter the main subject of the case',
    createFile: 'Create File',
    creating: 'Creating...',
    fileTitleRequired: 'File title is required',
    descriptionRequired: 'File description is required',
    caseNumberRequired: 'Case number is required',
    fileTypeRequired: 'File type is required',
    statusRequired: 'File status is required',
    selectRoleClientFirst: 'Please select a role and client first',
    editFile: 'Edit File',
    archiveFile: 'Archive File',
    unarchiveFile: 'Unarchive File',
    editFileTitle: 'Edit Legal File',
    archiveFileTitle: 'Archive File',
    unarchiveFileTitle: 'Unarchive File',
    archiveFileMessage: 'Are you sure you want to archive this file? The file will be moved to the archive.',
    unarchiveFileMessage: 'Are you sure you want to unarchive this file? The file will be restored to the main list.',
    confirmArchive: 'Confirm Archive',
    confirmUnarchive: 'Confirm Unarchive',
    cancelArchive: 'Cancel',
    cancelEdit: 'Cancel Edit',
    saveFile: 'Save File',
    saving: 'Saving...',
  },

  // Community
  community: {
    title: 'Community',
    subtitle: 'Join our community of lawyers, share experiences, and build a strong professional network',
    searchPlaceholder: 'Search community...',
    forums: 'Forums',
    events: 'Events',
    network: 'Professional Network',
    forumsDescription: 'Legal discussions',
    eventsDescription: 'Seminars & workshops',
    networkDescription: 'Connect with lawyers',
    // Forums
    createForum: 'Create Forum',
    allCategories: 'All Categories',
    noForums: 'No Forums Found',
    noForumsSearch: 'No forums match your search',
    createFirstForum: 'Start by creating your first forum',
    discussionForums: 'Discussion Forums',
    participateInDiscussions: 'Participate in legal discussions and seek advice from colleagues',
    // Events
    createEvent: 'Create Event',
    allTypes: 'All Types',
    allTime: 'All Time',
    upcoming: 'Upcoming',
    today: 'Today',
    thisWeek: 'This Week',
    noEvents: 'No Events Found',
    noEventsSearch: 'No events match your search',
    createFirstEvent: 'Start by creating your first event',
    professionalEvents: 'Professional Events',
    attendSeminars: 'Attend seminars, workshops, and professional meetings',
    // Network
    allSpecialties: 'All Specialties',
    allExperience: 'All Experience',
    connectedOnly: 'Connected Only',
    lawyers: 'lawyers',
    noLawyers: 'No Lawyers Found',
    noLawyersSearch: 'No lawyers match your search',
    noLawyersNetwork: 'No lawyers in the network yet',
    professionalNetwork: 'Professional Network',
    buildRelationships: 'Build a network of professional relationships with other lawyers',
    // Common
    viewDetails: 'View Details',
    viewProfile: 'View Profile',
    connect: 'Connect',
    connected: 'Connected',
    pending: 'Pending',
    declined: 'Declined',
    rsvp: 'RSVP',
    yearsExperience: 'years experience',
    languages: 'Languages',
    // Modals
    createNewForum: 'Create New Forum',
    createNewEvent: 'Create New Event',
    underDevelopment: 'This feature is under development',
    close: 'Close',
    // Forum Creation
    rules: 'Forum Rules',
    isPublic: 'Public Forum',
    allowThreadCreation: 'Allow Thread Creation',
    requireApproval: 'Require Approval for Threads',
    color: 'Color',
    icon: 'Icon',
    // Thread Types
    discussion: 'Discussion',
    question: 'Question',
    announcement: 'Announcement',
    poll: 'Poll',
    // Stats
    posts: 'posts',
    comments: 'comments',
    attendees: 'attendees',
    connections: 'connections',
    rating: 'rating',
    reviews: 'reviews',
    // Time
    lastPost: 'Last post',
    registeredAt: 'Registered at',
    // Status
    published: 'Published',
    cancelled: 'Cancelled',
    completed: 'Completed',
    draft: 'Draft',
    // Location
    onlineEvent: 'Online Event',
    link: 'Link',
    organizedBy: 'Organized by:',
    // Experience ranges
    experience0to2: '0-2 years',
    experience3to5: '3-5 years',
    experience6to10: '6-10 years',
    experience10plus: '10+ years',
  },

  events: {
    title: 'Agenda',
    calendarView: 'Calendar View',
    listView: 'List View',
    today: 'Today',
    noEvents: 'No events found',
    createNewEvent: 'Create New Event',
    editEvent: 'Edit Event',
    deleteEvent: 'Delete Event',
    addNewEvent: 'Add New Event',
    selectOffice: 'Select Office',
    selectClient: 'Select Client',
    selectLegalFile: 'Select Legal File',
    fileTitle: 'File Title',
    enterFileTitle: 'Enter file title',
    eventType: 'Event Type',
    eventTitle: 'Event Title',
    eventDate: 'Event Date',
    eventTime: 'Event Time',
    eventLocation: 'Event Location',
    eventDescription: 'Event Description',
    previousMonth: 'Previous Month',
    nextMonth: 'Next Month',
    previousMonthArrow: 'Previous',
    nextMonthArrow: 'Next',
    // Event types
    eventTypes: {
      clientMeeting: 'Client Meeting',
      courtSession: 'Court Session',
      governmental: 'Governmental',
    },
    // Event statuses
    eventStatuses: {
      upcoming: 'Upcoming',
      inProgress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      expired: 'Expired',
    },
    // Place types
    placeTypes: {
      office: 'Office',
      court: 'Court',
      clientLocation: 'Client Location',
      government: 'Government',
      other: 'Other',
    },
    // Calendar localization
    daysOfWeek: {
      sunday: 'Sunday',
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
    },
    daysOfWeekShort: {
      sunday: 'Sun',
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
    },
    months: {
      january: 'January',
      february: 'February',
      march: 'March',
      april: 'April',
      may: 'May',
      june: 'June',
      july: 'July',
      august: 'August',
      september: 'September',
      october: 'October',
      november: 'November',
      december: 'December',
    },
    monthsShort: {
      january: 'Jan',
      february: 'Feb',
      march: 'Mar',
      april: 'Apr',
      may: 'May',
      june: 'Jun',
      july: 'Jul',
      august: 'Aug',
      september: 'Sep',
      october: 'Oct',
      november: 'Nov',
      december: 'Dec',
    },
  },


  support: {
    title: 'Support Center',
    aboutMorafaa: 'About Morafaa System',
    aboutDescription: 'Morafaa System is a comprehensive legal practice management platform designed specifically for lawyers and legal professionals. Our system streamlines case management, client relationships, document handling, and financial tracking, enabling you to focus on what matters most - delivering exceptional legal services to your clients. With integrated calendar management, automated billing, and secure document storage, Morafaa empowers legal professionals to build more efficient and successful practices.',
    connectWithUs: 'Connect With Us',
    facebook: 'Follow us on Facebook',
    instagram: 'Follow us on Instagram',
    customerSupport: 'Customer Support',
    whatsappSupport: 'WhatsApp Support',
    legalInformation: 'Legal Information',
    termsConditions: 'Terms & Conditions',
    privacyPolicy: 'Privacy Policy',
    viewTerms: 'View Terms & Conditions',
    viewPrivacy: 'View Privacy Policy',
    english: 'English',
    arabic: 'Arabic',
    contactUs: 'Contact Us',
    inquiryType: 'Type',
    selectType: 'Select Type',
    message: 'Message',
    messagePlaceholder: 'Write your message here...',
    send: 'Send',
    sending: 'Sending...',
    inquirySent: 'Inquiry Sent',
    inquirySentMessage: 'Your inquiry has been sent successfully. We will contact you soon.',
    errorSending: 'Error Sending',
    errorSendingMessage: 'An error occurred while sending your inquiry. Please try again.',
  },

  referral: {
    title: 'Referral Program',
    subtitle: 'Invite a lawyer → earn 1 Ad-Free month',
    headerDescription: 'Share Morafaa with fellow lawyers and earn one Ad-Free month per successful referral',
    howItWorks: 'How it works',
    step1: 'Share your invitation code or link with fellow lawyers.',
    step2: 'They sign up with your code and complete their first session within 7 days.',
    step3: 'You get 1 Ad-Free month per successful referral — up to 10 per calendar month.',
    yourCode: 'Your invitation code',
    yourInviteLink: 'Invite link',
    copy: 'Copy code',
    copyLink: 'Copy link',
    share: 'Share',
    copied: 'Copied',
    totalReferrals: 'Successful referrals',
    successfulReferrals: 'Peers who signed up and opened the app',
    freeMonthsEarned: 'Ad-Free months earned',
    monthsOfFreeService: 'Months without ads',
    termsTitle: 'Terms & Conditions:',
    terms: [
      'A successful referral is counted after the invitee signs up with your code and completes a first session within 7 days',
      'One Ad-Free month is added to your account (source: referral)',
      'Maximum 10 rewarded referrals per calendar month per referrer',
      'Self-referrals and duplicate referred accounts do not count',
      'Invitation codes are personal — avoid blasting them publicly',
      'Morafaa may modify or end this program at any time'
    ],
    shareMessage: 'Hi!\n\nI\'d like to invite you to join Morafaa.\n\nAbout Morafaa: an all-in-one legal practice platform for Egyptian lawyers — clients, cases, calendar, legal library, and Lawyer Guide, on mobile and web.\n\nInvitation code:',
    shareTitle: 'Join Morafaa',
    statsEmpty: 'No successful referrals yet',
    statsError: 'Could not load counts. Tap refresh.',
    statsRefresh: 'Refresh',
  },


  teamMembers: {
    title: 'Team Members',
    selectOffice: 'Select Office',
    teamMembersList: 'Team Members List',
    memberDetails: 'Member Details',
    memberName: 'Member Name',
    memberEmail: 'Email',
    memberPhone: 'Phone Number',
    memberRole: 'Member Role',
    permissions: 'Permissions',
    accessClients: 'Access Clients',
    accessLegalFiles: 'Access Legal Files',
    accessEvents: 'Access Events',
    noMembers: 'No team members found',
    noOfficeSelected: 'No office selected',
    addMember: 'Add Member',
    editMember: 'Edit Member',
    removeMember: 'Remove Member',
    updatePermissions: 'Update Permissions',
    permissionsUpdated: 'Permissions updated successfully!',
    memberAdded: 'Member added successfully!',
    memberRemoved: 'Member removed successfully!',
    errorUpdatingPermissions: 'Failed to update permissions. Please try again.',
    errorAddingMember: 'Failed to add member. Please try again.',
    errorRemovingMember: 'Failed to remove member. Please try again.',
  },

  lawyerGuide: {
    title: 'Lawyer Guide',
    subtitle: 'Quick access to essential legal resources and tools',
    categories: 'Categories',
    resources: 'Resources',
    searchPlaceholder: 'Search categories and resources...',
    noCategories: 'No categories available',
    noResources: 'No resources available',
    selectCategory: 'Select a category to view resources',
    noResults: 'No results found',
    openInNewTab: 'Open in new tab',
    externalLink: 'External link',
    categoryDescription: 'Category description',
    resourceDescription: 'Resource description',
    loadingCategories: 'Loading categories...',
    loadingResources: 'Loading resources...',
    errorLoadingCategories: 'Failed to load categories',
    errorLoadingResources: 'Failed to load resources',
    searchFailed: 'Search failed',
  },

  subscriptions: {
    title: 'Subscriptions',
    subtitle: 'Manage your subscriptions and payment plans for the best Morafaa experience',
    currentSubscription: 'Your Current Subscription',
    usageDashboard: 'Usage Dashboard',
    planComparison: 'Plan Comparison',
    noActiveSubscription: 'No Active Subscription',
    noActiveSubscriptionMessage: 'Choose a subscription plan to start using all features',
    viewPlans: 'View Plans',
    manageSubscription: 'Manage Subscription',
    refreshData: 'Refresh Data',
    quickActions: 'Quick Actions',
    upgradePlan: 'Upgrade Plan',
    choosePlan: 'Choose Plan',
    currentPlan: 'Your Plan',
    mostPopular: 'Most Popular',
    freeTrial: 'Free Trial',
    dayTrial: 'day trial',
    daysLeft: 'days left',
    trialDaysLeft: 'Trial Days Left',
    startDate: 'Start Date',
    endDate: 'End Date',
    autoRenew: 'Auto-Renew',
    yes: 'Yes',
    no: 'No',
    active: 'Active',
    trial: 'Trial',
    expired: 'Expired',
    cancelled: 'Cancelled',
    pastDue: 'Past Due',
    loading: 'Loading...',
    loadingSubscription: 'Loading subscription data...',
    loadingUsageStats: 'Loading usage statistics...',
    failedToLoad: 'Failed to load subscription data',
    tryAgain: 'Try Again',
    close: 'Close',
    // Plan features
    planFeatures: 'Plan Features',
    offices: 'Offices',
    clients: 'Clients',
    files: 'Files',
    unlimited: 'Unlimited',
    perOffice: 'per Office',
    perClient: 'per Client',
    eventReminders: 'Event Reminders',
    advancedStats: 'Advanced Statistics',
    communityAccess: 'Community Access',
    phoneSupport: 'Phone Support',
    teamMembers: 'Team Members',
    perMember: 'per member',
    // Usage warnings
    usageWarnings: 'Usage Warnings',
    approachingLimit: 'Approaching limit',
    // Plan names
    freePlan: 'Free Plan',
    basicPlan: 'Basic Plan',
    professionalPlan: 'Professional Plan',
    // Plan descriptions
    freePlanDescription: 'Perfect for individual lawyers starting out.',
    basicPlanDescription: 'Ideal for growing law practices',
    professionalPlanDescription: 'Comprehensive features for established law firms.',
  },

  offices: {
    title: 'Offices Management',
    subtitle: 'Manage and track all law offices and branches',
    loading: 'Loading offices...',
    loadingOffices: 'Loading offices...',
    errorTitle: 'Error Loading Offices',
    loadError: 'Failed to load offices from database. Please try again.',
    demoMode: 'Demo Mode: Showing demo data due to database fetch failure. Click "Reload" to try again.',
    exportExcel: 'Export Excel',
    refreshing: 'Refreshing...',
    refresh: 'Refresh',
    allStatus: 'All Status',
    activeStatus: 'Active',
    archived: 'Archived',
    deleted: 'Deleted',
    noOffices: 'No offices found',
    noOfficesFound: 'No offices found',
    tryAdjusting: 'Try adjusting your search or filter criteria',
    noOfficesCreated: 'No offices have been created yet',
    officeDetails: 'Office Details',
    contactInformation: 'Contact Information',
    address: 'Address',
    phone: 'Phone',
    email: 'Email',
    website: 'Website',
    statusDates: 'Status & Dates',
    status: 'Status',
    created: 'Created',
    updated: 'Updated',
    officeStaff: 'Office Staff',
    loadingStaff: 'Loading staff...',
    retry: 'Retry',
    noStaffMembers: 'No staff members',
    noStaffAssigned: 'This office doesn\'t have any staff members assigned yet',
    inactive: 'Inactive',
    activeStaff: 'Active',
    noPhone: 'No phone',
    noEmail: 'No email',
    officeName: 'Office Name',
    alternativeName: 'Alternative Name',
    description: 'Description',
    optionalEmail: 'Email Address (Optional)',
    officeDescription: 'Office Description',
    logoURL: 'Logo URL',
    briefDescription: 'Brief description of the office...',
    logoPlaceholder: 'https://example.com/logo.png',
    searchPlaceholder: 'Search offices...',
    failedToLoadUsers: 'Failed to load office users',
    // Add Office Modal
    searching: 'Searching...',
    selectSubSpecialty: 'Select sub-specialty',
    lawyerTitle: 'Lawyer Title',
    salaryConfiguration: 'Salary Configuration',
    salaryType: 'Salary Type',
    studentIntern: 'Student/Intern (No Salary)',
    fixedSalary: 'Fixed Salary',
    percentageBased: 'Percentage Based',
    fixedAndPercentage: 'Fixed + Percentage',
    fixedSalaryEGP: 'Fixed Salary (EGP)',
    profitPercentage: 'Profit Percentage (%)',
    officeNameRequired: 'Office name is required',
    phoneNumberRequired: 'Phone number is required',
    validEmailAddress: 'Please enter a valid email address',
    validSalaryAmount: 'Please enter a valid salary amount',
    validProfitPercentage: 'Please enter a valid profit percentage',
    // Sub-specialty options
    personalStatus: 'Personal Status Law',
    commercial: 'Commercial Law',
    criminal: 'Criminal Law',
    civil: 'Civil Law',
    administrative: 'Administrative Law',
    labor: 'Labor Law',
    tax: 'Tax Law',
    realEstate: 'Real Estate Law',
    intellectualProperty: 'Intellectual Property Law',
    family: 'Family Law',
  },
};

// Language detection utility - defaults to Arabic
export const getCurrentLanguage = (): 'ar' | 'en' => {
  const lang = navigator.language || (navigator as any).userLanguage;
  return lang.startsWith('ar') ? 'ar' : 'ar'; // Default to Arabic instead of English
};

// Get localized strings based on language
export const getStrings = (language: 'ar' | 'en' = 'ar'): LocalizedStrings => {
  return language === 'ar' ? stringsAr : stringsEn;
};
